'use client';

import React from 'react';
import {Link} from '@/i18n/navigation';
import {Box, Container, FormControlLabel, Typography, useMediaQuery, useTheme} from '@mui/material';
import {
  FormulaFieldFragment,
  FormulaGroupFragment,
  FormulaSectionFieldsFragment
} from '@generated/graphql';
import {
  StyledFieldLabel,
  StyledFieldWrapper,
  StyledFormSectionContainer,
  StyledFormSectionForm,
  StyledFormSectionRichTextContainer,
  StyledFormTextArea,
  StyledFormTextField,
  StyledFormCheckbox,
  StyledGroupFieldContainer,
  StyledGroupContainer,
  StyledGroupFieldsContainer,
  StyledFormSubmitButton, StyledSuccessContainer
} from '@/components/sections/form-section/FormSection.styles';
import {RichTextRenderer} from '@/components/contentful/RichTextRenderer';
import {useTranslations} from 'next-intl';
import SuccessCheckIcon from '@/components/ui/icons/SuccessCheck';
import fetchJsonp from 'fetch-jsonp';
import {trackFormSubmit, trackFormError} from '@/lib/analytics';

type ValidFormField = FormulaFieldFragment & {
  type: string;
  name: string;
  label: string;
};

function isTextAreaField(field: ValidFormField): boolean {
  return field.type.toLowerCase() === 'textarea';
}

function isCheckbox(field: ValidFormField): boolean {
  return field.type.toLowerCase() === 'checkbox';
}

const isValidFormField = (
  field: FormulaFieldFragment | null | undefined
): field is ValidFormField => {
  if (!field) return false;
  return (
    typeof field.type === 'string' &&
    field.type.trim() !== '' &&
    typeof field.name === 'string' &&
    field.name.trim() !== '' &&
    typeof field.label === 'string' &&
    field.label.trim() !== ''
  );
};

type ValidFormGroup = FormulaGroupFragment & {
  formFieldsCollection: NonNullable<FormulaGroupFragment['formFieldsCollection']>;
  validFields: ValidFormField[];
};

type RawFormCtaButton = FormulaSectionFieldsFragment['formCtaButton'];

type ValidFormCtaButton = NonNullable<RawFormCtaButton> & {
  label: string;
  actionLink: string;
};

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

const isValidFormCtaButton = (ctaButton: RawFormCtaButton): ctaButton is ValidFormCtaButton => {
  return (
    !!ctaButton &&
    isNonEmptyString(ctaButton.actionLink) &&
    isNonEmptyString(ctaButton.label)
  );
};

// Replace the mutating type-guard approach with a pure mapping + filtering function.
function toValidFormGroup(group: FormulaGroupFragment | null | undefined): ValidFormGroup | null {
  if (!group?.formFieldsCollection) return null;

  const validFields = (group.formFieldsCollection.items ?? []).filter(isValidFormField);
  if (validFields.length === 0) return null;

  return {
    ...group,
    formFieldsCollection: group.formFieldsCollection,
    validFields,
  };
}

function getEmailDomain(email: string): string | null {
  const at = email.lastIndexOf('@');
  if (at === -1) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return domain || null;
}

function isLikelyPersonalEmail(email: string): boolean {
  const domain = getEmailDomain(email);
  if (!domain) return false;

  // A pragmatic denylist of common consumer providers.
  // Extend as needed.
  const personalDomains = new Set([
    'gmail.com',
    'googlemail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'yahoo.com',
    'yahoo.se',
    'proton.me',
    'protonmail.com',
    'pm.me',
  ]);

  return personalDomains.has(domain);
}

const checkboxIcon = (
  <Box sx={{
    width: 20,
    height: 20,
    borderRadius: '4px',
    border: '2px solid #f66',
    backgroundColor: 'white',
  }} />
);

const checkboxCheckedIcon = (
  <Box sx={{
    width: 20,
    height: 20,
    backgroundColor: '#f66',
    borderRadius: '4px',
    border: '2px solid #f66',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <svg width='12' height='9' viewBox='0 0 14 10' fill='none'>
      <path d='M1 5L5 9L13 1' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'/>
    </svg>
  </Box>
);

function CheckboxField({
  field,
  checked,
  onChange,
}: {
  field: ValidFormField;
  checked: boolean;
  onChange: (_value: boolean) => void;
}) {
  return (
    <>
      <input type='hidden' name={field.name} value={checked ? 'yes' : ''} />
      <FormControlLabel
        control={
          <StyledFormCheckbox
            checked={checked}
            onChange={(_, val) => onChange(val)}
            icon={checkboxIcon}
            checkedIcon={checkboxCheckedIcon}
          />
        }
        label={field.label}
        sx={{ color: 'inherit', gap: 1, width: '100%', display: 'flex', alignItems: 'flex-start' }}
      />
    </>
  );
}

function fieldIdFromKey(key: string): string {
  return `formfield-${key.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function renderField(f: ValidFormField, key: string, showRequired: boolean) {
  const id = fieldIdFromKey(key);
  const required = !!f.isRequired;
  const showAsterisk = showRequired && required;
  const labelEl = (
    <StyledFieldLabel htmlFor={id}>
      {f.label}
      {showAsterisk && <span aria-hidden='true'> *</span>}
    </StyledFieldLabel>
  );

  if (isTextAreaField(f)) {
    return (
      <StyledFieldWrapper key={key}>
        {labelEl}
        <StyledFormTextArea
          id={id}
          name={f.name}
          required={required}
          aria-required={required}
        />
      </StyledFieldWrapper>
    );
  }

  return (
    <StyledFieldWrapper key={key}>
      {labelEl}
      <StyledFormTextField
        id={id}
        type={f.type}
        name={f.name}
        required={required}
        slotProps={{htmlInput: {'aria-required': required}}}
      />
    </StyledFieldWrapper>
  );
}

type FormSectionProps = {
    section: FormulaSectionFieldsFragment;
};

export default function FormSection({ section }: FormSectionProps) {
  const theme = useTheme();
  const t = useTranslations('FormSection');
  const formGroups = section.groupsCollection?.items || [];
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'));

  const validFormGroups: ValidFormGroup[] = formGroups
    .map(toValidFormGroup)
    .filter((g): g is ValidFormGroup => g != null);

  // Extract all checkbox fields across all groups
  const checkboxFields = validFormGroups.flatMap((g) =>
    g.validFields.filter(isCheckbox).map((f, idx) => ({ field: f, key: `cb-${f.name}-${g.sys.id}-${idx}` }))
  );

  // Remove checkbox fields from the groups for the grid layout
  const nonCheckboxGroups: ValidFormGroup[] = validFormGroups
    .map((g) => {
      const nonCheckboxFields = g.validFields.filter((f) => !isCheckbox(f));
      if (nonCheckboxFields.length === 0) return null;
      return { ...g, validFields: nonCheckboxFields };
    })
    .filter((g): g is ValidFormGroup => g != null);

  // Count the submit CTA as one extra item in the second column when splitting.
  const totalItemsIncludingCta = nonCheckboxGroups.length + 1;
  const splitIndex = Math.ceil(totalItemsIncludingCta / 2);
  const firstGroups = nonCheckboxGroups.slice(0, splitIndex);
  const secondGroups = nonCheckboxGroups.slice(splitIndex);

  const rawCtaButton = section.formCtaButton;
  const ctaButton: ValidFormCtaButton | null = isValidFormCtaButton(rawCtaButton)
    ? rawCtaButton
    : null;
  
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [checkboxStates, setCheckboxStates] = React.useState<Record<string, boolean>>({});

  const allCheckboxesChecked = checkboxFields.every(({field}) => checkboxStates[field.name]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!ctaButton) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const form = event.currentTarget;
      const formData = new FormData(form);
      const values = Object.fromEntries(formData.entries());

      // Validate: email must be a company email (not a common personal provider)
      const emailFieldName = validFormGroups
        .flatMap((g) => g.validFields)
        .find((f) => f.type.toLowerCase() === 'email')?.name;

      if (emailFieldName) {
        const rawEmail = values[emailFieldName];
        const email = typeof rawEmail === 'string' ? rawEmail : '';
        if (email && isLikelyPersonalEmail(email)) {
          trackFormError({formName: section.heading || 'contact_form', errorType: 'personal_email', contentId: section.sys.id});
          alert(t('useValidEmail'));
          return;
        }
      }

      // --- JSONP submission ---
      const queryString = new URLSearchParams(values as Record<string, string>).toString();

      const callbackName = 'callback';
      // JSONP response is expected to call the global function named in `callbackName` with the response data.
      const data = await fetchJsonp(`${ctaButton.actionLink}?${queryString}`, {
        jsonpCallback: callbackName,
        jsonpCallbackFunction: callbackName,
        timeout: 10000,
      })
        .then((response) => response.json());

      // Check success (adjust according to your server's response structure)
      if (data === 'success' || data.result === 'success') {
        trackFormSubmit({formName: section.heading || 'contact_form', contentId: section.sys.id});
        setSubmitSuccess(true);
        form.reset();
      } else {
        alert(t('error'));
      }

    } catch (e) {
      console.error('Form submission error:', e);
      trackFormError({formName: section.heading || 'contact_form', errorType: 'submission_error', contentId: section.sys.id});
      alert(t('error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!ctaButton || validFormGroups.length <= 0) {
    return null;
  }
  
  if(submitSuccess) {
    return (
      <StyledFormSectionContainer>
        <Container maxWidth={'mdlg'}>
          <StyledSuccessContainer>
            <Typography variant='h3' align='center' sx={{mb: 4}}>
              {section.formSuccessText || t('thanks')}
            </Typography>
            <SuccessCheckIcon size={isMdUp ? 80 : 56}/>
          </StyledSuccessContainer>
        </Container>
      </StyledFormSectionContainer>
    )
  }

  return (
    <StyledFormSectionContainer  {...section.id && {id: section.id}}>
      <Container maxWidth={'mdlg'}>
        <StyledFormSectionForm onSubmit={handleSubmit}>
          {section.heading && (
            <Typography variant='h2' align='center' sx={{mb: 2}}>
              {section.heading}
            </Typography>
          )}
          {section.breadText?.json && (
            <StyledFormSectionRichTextContainer maxWidth={'sm'}>
              <RichTextRenderer useSecondaryLinkColor richTextDocument={section.breadText?.json}/>
            </StyledFormSectionRichTextContainer>
          )}
          {checkboxFields.length > 0 && (
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              {checkboxFields.map(({ field, key }) => (
                <CheckboxField
                  key={key}
                  field={field}
                  checked={!!checkboxStates[field.name]}
                  onChange={(val) => setCheckboxStates((prev) => ({ ...prev, [field.name]: val }))}
                />
              ))}
            </Box>
          )}
          <StyledGroupContainer singleGroup={secondGroups.length === 0}>
            <StyledGroupFieldsContainer>
              {firstGroups.map((g) => (
                <StyledGroupFieldContainer key={g.sys.id}>
                  {g.validFields.map((f, idx) =>
                    renderField(f, `${f.name}-${f.label}-${g.sys.id}-${idx}`, true)
                  )}
                </StyledGroupFieldContainer>
              ))}
            </StyledGroupFieldsContainer>

            <StyledGroupFieldsContainer>
              {secondGroups.map((g) => (
                <StyledGroupFieldContainer key={g.sys.id}>
                  {g.validFields.map((f, idx) =>
                    renderField(f, `${f.name}-${f.label}-${g.sys.id}-${idx}`, true)
                  )}
                </StyledGroupFieldContainer>
              ))}

              <StyledGroupFieldContainer sx={{ justifyContent: 'flex-end', mt: 'auto' }}>
                <StyledFormSubmitButton type='submit' variant='contained' disabled={isSubmitting || !allCheckboxesChecked}>
                  {isSubmitting ? `${ctaButton.label}...` : ctaButton.label}
                </StyledFormSubmitButton>
              </StyledGroupFieldContainer>
            </StyledGroupFieldsContainer>
          </StyledGroupContainer>
          <Typography variant='body2' sx={{ mt: 5, textAlign: 'center' }}>
            {t.rich('privacyText', {
              link: (chunks) => (
                <Link href='/integritetspolicy' style={{ color: '#f66', textDecoration: 'underline' }}>
                  {chunks}
                </Link>
              ),
            })}
          </Typography>
        </StyledFormSectionForm>
      </Container>
    </StyledFormSectionContainer>
  );
}
